import { useState } from "react";
import * as api from "../../api/client";
import { Alert, Chip, Filters, List, Pager, Stat, Stats } from "./ui";
import { code, pct, TONE, usePaged, when } from "./lib";

const FILTERS = [
  ["", "Hammasi"],
  ["questionnaire", "Savolnoma"],
  ["intake", "Kiritish"],
  ["open", "Tugallanmagan"],
  ["refined", "Kod aniqlashtirildi"],
  ["mismatch", "Mos emas / noaniq"],
  ["low", "Past ishonch"],
];

const VERDICT = {
  tasdiqlandi: ["tasdiqlandi", TONE.ok],
  aniqlashtirildi: ["aniqlashtirildi", TONE.info],
  mos_emas: ["mos emas", TONE.bad],
  noaniq: ["noaniq", TONE.warn],
};

const CONFIDENCE = {
  high: ["ishonch yuqori", TONE.ok],
  medium: ["ishonch o'rta", TONE.info],
  low: ["ishonch past", TONE.warn],
};

const PAGE = 25;

/** Savolnoma (kodni ha/yo'q bilan tekshirish) va yo'riqnomali kiritish (nomdan kodgacha). */
export default function FormsTab({ days, refresh, preset = "" }) {
  const [kind, setKind] = useState(preset);
  const [stats, setStats] = useState(null);

  const list = usePaged(
    (page) =>
      api.reviewForms({ days, kind, limit: PAGE, offset: page * PAGE }).then((r) => {
        setStats(r);
        return r;
      }),
    [kind, days],
    refresh,
  );

  const q = stats?.questionnaire;
  const ik = stats?.intake;
  const mismatch = q && !q.error ? q.mismatch + q.unclear : 0;

  return (
    <div className="space-y-5">
      <Alert>{list.error}</Alert>

      {stats && (
        <Stats>
          {q?.error ? (
            <Stat label="Savolnoma" value="—" hint={q.error} />
          ) : (
            <>
              <Stat label="Savolnoma" value={q.total} hint={`${q.done} yakunlandi`} />
              <Stat
                label="Tasdiqlandi"
                value={q.confirmed}
                hint={q.done ? `${pct(q.confirmed, q.done)}%` : ""}
                tone={q.confirmed ? "text-emerald-300" : ""}
              />
              <Stat label="Aniqlashtirildi" value={q.refined} hint="boshqa bargga o'tdi" />
              <Stat label="Mos emas" value={mismatch} hint="noaniq bilan" tone={mismatch ? "text-rose-300" : ""} />
              <Stat label="Savol / sessiya" value={q.avg_questions} />
            </>
          )}
          {ik?.error ? (
            <Stat label="Kiritish" value="—" hint={ik.error} />
          ) : (
            <Stat
              label="Kiritish"
              value={ik.total}
              hint={`${ik.done} yakunlandi · ${ik.low} past ishonch`}
              tone={ik.low ? "text-amber-300" : ""}
            />
          )}
        </Stats>
      )}

      <Filters items={FILTERS} value={kind} onChange={setKind} />

      <List loading={list.loading} items={list.items}>
        <div className="overflow-x-auto rounded-xl border border-sky-800/40">
          <table className="w-full min-w-[640px] text-left text-[11px]">
            <thead className="bg-[#041228] text-sky-400/70">
              <tr>
                <th className="px-3 py-2 font-medium">Tovar</th>
                <th className="px-3 py-2 font-medium">Berilgan kod</th>
                <th className="px-3 py-2 font-medium">Natija</th>
                <th className="px-3 py-2 font-medium">Savollar</th>
                <th className="px-3 py-2 font-medium">Holat</th>
                <th className="px-3 py-2 font-medium">Vaqt</th>
              </tr>
            </thead>
            <tbody>
              {list.items.map((r) => (
                <Row key={`${r.kind}-${r.id}`} r={r} />
              ))}
            </tbody>
          </table>
        </div>
      </List>

      <Pager page={list.page} setPage={list.setPage} full={list.items.length === PAGE} />
    </div>
  );
}

function Row({ r }) {
  const isQ = r.kind === "questionnaire";
  const changed = isQ && r.result_code && r.result_code !== r.input_code;
  const [label, cls] = r.status === "savollar"
    ? ["tugallanmagan", TONE.dim]
    : isQ
      ? VERDICT[r.verdict] ?? [r.verdict || "yakunlandi", TONE.dim]
      : CONFIDENCE[r.confidence] ?? ["yakunlandi", TONE.dim];
  return (
    <tr className="border-t border-sky-900/40 bg-[#030f24]/60">
      <td className="max-w-[260px] px-3 py-2">
        <div className="truncate text-slate-200">{r.name || "—"}</div>
        <div className="text-[10px] text-sky-400/50">
          {isQ ? "savolnoma" : "kiritish"}
          {r.ref && ` · ${r.ref}`}
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-2 font-mono text-sky-200">{isQ ? code(r.input_code) : "—"}</td>
      <td className={`whitespace-nowrap px-3 py-2 font-mono ${changed ? "text-amber-300" : "text-emerald-300"}`}>
        {r.result_code ? code(r.result_code) : "—"}
      </td>
      <td className="px-3 py-2 text-sky-300/80">{isQ ? `${r.answered}/${r.questions}` : "—"}</td>
      <td className="px-3 py-2">
        <Chip cls={cls}>{label}</Chip>
      </td>
      <td className="whitespace-nowrap px-3 py-2 font-mono text-[10px] text-sky-400/50">{when(r.created_at)}</td>
    </tr>
  );
}
